// Kleiner QR-Code-Erzeuger für den Zwei-Faktor-Schlüssel (Agent 8C): Byte-Modus, Fehlerkorrektur M, Version 1 bis 10 (bis 213 Bytes).
// Ohne Zusatzpaket, damit die Oberfläche keine neue Abhängigkeit braucht. Aufbau nach ISO/IEC 18004; die Ausgabe ist gegen eine
// Referenzbibliothek verglichen und mit einem Decoder gelesen worden (siehe STAND.md, Abschnitt 8C).

/** Datenwörter, Fehlerkorrekturwörter je Block und Blockaufbau (Anzahl, Datenwörter) für Stufe M, Version 1 bis 10 */
const M_TABELLE: { ec: number; bloecke: [number, number][] }[] = [
  { ec: 10, bloecke: [[1, 16]] },
  { ec: 16, bloecke: [[1, 28]] },
  { ec: 26, bloecke: [[1, 44]] },
  { ec: 18, bloecke: [[2, 32]] },
  { ec: 24, bloecke: [[2, 43]] },
  { ec: 16, bloecke: [[4, 27]] },
  { ec: 18, bloecke: [[4, 31]] },
  { ec: 22, bloecke: [[2, 38], [2, 39]] },
  { ec: 22, bloecke: [[3, 36], [2, 37]] },
  { ec: 26, bloecke: [[4, 43], [1, 44]] },
];
const AUSRICHTUNG: number[][] = [[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]];

// Galois-Körper GF(256), Polynom 0x11D
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 256) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
}
const mal = (a: number, b: number): number => (a && b ? EXP[LOG[a] + LOG[b]] : 0);

function teiler(grad: number): number[] {
  const ergebnis = new Array<number>(grad).fill(0);
  ergebnis[grad - 1] = 1;
  let wurzel = 1;
  for (let i = 0; i < grad; i++) {
    for (let j = 0; j < ergebnis.length; j++) {
      ergebnis[j] = mal(ergebnis[j], wurzel);
      if (j + 1 < ergebnis.length) ergebnis[j] ^= ergebnis[j + 1];
    }
    wurzel = mal(wurzel, 2);
  }
  return ergebnis;
}

function rest(daten: number[], t: number[]): number[] {
  const ergebnis = t.map(() => 0);
  for (const b of daten) {
    const faktor = b ^ (ergebnis.shift() as number);
    ergebnis.push(0);
    t.forEach((koeff, i) => {
      ergebnis[i] ^= mal(koeff, faktor);
    });
  }
  return ergebnis;
}

const bit = (wert: number, i: number): boolean => ((wert >>> i) & 1) !== 0;

function datenwoerter(bytes: number[], version: number): number[] {
  const bits: number[] = [];
  const schreibe = (wert: number, anzahl: number) => {
    for (let i = anzahl - 1; i >= 0; i--) bits.push((wert >>> i) & 1);
  };
  const info = M_TABELLE[version - 1];
  const kapazitaet = info.bloecke.reduce((s, [n, d]) => s + n * d, 0) * 8;
  schreibe(0b0100, 4);
  schreibe(bytes.length, version < 10 ? 8 : 16);
  bytes.forEach((b) => schreibe(b, 8));
  schreibe(0, Math.min(4, kapazitaet - bits.length));
  while (bits.length % 8) bits.push(0);
  const woerter: number[] = [];
  for (let i = 0; i < bits.length; i += 8) woerter.push(parseInt(bits.slice(i, i + 8).join(""), 2));
  for (let pad = 0xec; woerter.length < kapazitaet / 8; pad ^= 0xec ^ 0x11) woerter.push(pad);
  return woerter;
}

function mitFehlerkorrektur(daten: number[], version: number): number[] {
  const { ec, bloecke } = M_TABELLE[version - 1];
  const t = teiler(ec);
  const datenBloecke: number[][] = [];
  let pos = 0;
  for (const [anzahl, laenge] of bloecke) {
    for (let i = 0; i < anzahl; i++) {
      datenBloecke.push(daten.slice(pos, pos + laenge));
      pos += laenge;
    }
  }
  const ecBloecke = datenBloecke.map((b) => rest(b, t));
  const ergebnis: number[] = [];
  const laengster = Math.max(...datenBloecke.map((b) => b.length));
  for (let i = 0; i < laengster; i++) datenBloecke.forEach((b) => i < b.length && ergebnis.push(b[i]));
  for (let i = 0; i < ec; i++) ecBloecke.forEach((b) => ergebnis.push(b[i]));
  return ergebnis;
}

class Matrix {
  readonly groesse: number;
  readonly dunkel: boolean[][];
  readonly fest: boolean[][];

  constructor(readonly version: number) {
    this.groesse = version * 4 + 17;
    this.dunkel = Array.from({ length: this.groesse }, () => new Array<boolean>(this.groesse).fill(false));
    this.fest = Array.from({ length: this.groesse }, () => new Array<boolean>(this.groesse).fill(false));
    this.funktionsmuster();
  }

  private setze(x: number, y: number, dunkel: boolean): void {
    this.dunkel[y][x] = dunkel;
    this.fest[y][x] = true;
  }

  private funktionsmuster(): void {
    const n = this.groesse;
    for (let i = 0; i < n; i++) {
      this.setze(6, i, i % 2 === 0);
      this.setze(i, 6, i % 2 === 0);
    }
    this.sucher(3, 3);
    this.sucher(n - 4, 3);
    this.sucher(3, n - 4);
    const pos = AUSRICHTUNG[this.version - 1];
    const k = pos.length;
    for (let i = 0; i < k; i++) {
      for (let j = 0; j < k; j++) {
        if ((i === 0 && j === 0) || (i === 0 && j === k - 1) || (i === k - 1 && j === 0)) continue;
        this.ausrichtung(pos[i], pos[j]);
      }
    }
    this.formatBits(0);
    this.versionBits();
  }

  private sucher(x: number, y: number): void {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        const xx = x + dx;
        const yy = y + dy;
        if (xx >= 0 && xx < this.groesse && yy >= 0 && yy < this.groesse) this.setze(xx, yy, d !== 2 && d !== 4);
      }
    }
  }

  private ausrichtung(x: number, y: number): void {
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) this.setze(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
  }

  formatBits(maske: number): void {
    const n = this.groesse;
    const daten = (0 << 3) | maske; // Stufe M = 00
    let r = daten;
    for (let i = 0; i < 10; i++) r = (r << 1) ^ ((r >>> 9) * 0x537);
    const bits = ((daten << 10) | r) ^ 0x5412;
    for (let i = 0; i <= 5; i++) this.setze(8, i, bit(bits, i));
    this.setze(8, 7, bit(bits, 6));
    this.setze(8, 8, bit(bits, 7));
    this.setze(7, 8, bit(bits, 8));
    for (let i = 9; i < 15; i++) this.setze(14 - i, 8, bit(bits, i));
    for (let i = 0; i < 8; i++) this.setze(n - 1 - i, 8, bit(bits, i));
    for (let i = 8; i < 15; i++) this.setze(8, n - 15 + i, bit(bits, i));
    this.setze(8, n - 8, true);
  }

  private versionBits(): void {
    if (this.version < 7) return;
    let r = this.version;
    for (let i = 0; i < 12; i++) r = (r << 1) ^ ((r >>> 11) * 0x1f25);
    const bits = (this.version << 12) | r;
    for (let i = 0; i < 18; i++) {
      const a = this.groesse - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.setze(a, b, bit(bits, i));
      this.setze(b, a, bit(bits, i));
    }
  }

  platziere(woerter: number[]): void {
    const n = this.groesse;
    let i = 0;
    for (let rechts = n - 1; rechts >= 1; rechts -= 2) {
      if (rechts === 6) rechts = 5;
      for (let senkrecht = 0; senkrecht < n; senkrecht++) {
        for (let j = 0; j < 2; j++) {
          const x = rechts - j;
          const hoch = ((rechts + 1) & 2) === 0;
          const y = hoch ? n - 1 - senkrecht : senkrecht;
          if (!this.fest[y][x] && i < woerter.length * 8) {
            this.dunkel[y][x] = bit(woerter[i >>> 3], 7 - (i & 7));
            i++;
          }
        }
      }
    }
  }

  maskieren(maske: number): void {
    for (let y = 0; y < this.groesse; y++) {
      for (let x = 0; x < this.groesse; x++) {
        let umkehren: boolean;
        switch (maske) {
          case 0: umkehren = (x + y) % 2 === 0; break;
          case 1: umkehren = y % 2 === 0; break;
          case 2: umkehren = x % 3 === 0; break;
          case 3: umkehren = (x + y) % 3 === 0; break;
          case 4: umkehren = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: umkehren = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: umkehren = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: umkehren = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
        }
        if (!this.fest[y][x] && umkehren) this.dunkel[y][x] = !this.dunkel[y][x];
      }
    }
  }

  strafpunkte(): number {
    const n = this.groesse;
    const m = this.dunkel;
    let punkte = 0;
    // Regel 1: Läufe gleicher Farbe (waagrecht und senkrecht), Regel 3: Suchermuster
    const muster = (zeile: boolean[]): number => {
      let p = 0;
      let lauf = 1;
      for (let i = 1; i <= zeile.length; i++) {
        if (i < zeile.length && zeile[i] === zeile[i - 1]) lauf++;
        else {
          if (lauf >= 5) p += 3 + (lauf - 5);
          lauf = 1;
        }
      }
      const s = zeile.map((b) => (b ? "1" : "0")).join("");
      for (const m3 of ["10111010000", "00001011101"]) {
        for (let i = s.indexOf(m3); i >= 0; i = s.indexOf(m3, i + 1)) p += 40;
      }
      return p;
    };
    for (let y = 0; y < n; y++) punkte += muster(m[y]);
    for (let x = 0; x < n; x++) punkte += muster(m.map((z) => z[x]));
    // Regel 2: 2x2-Blöcke
    for (let y = 0; y < n - 1; y++) for (let x = 0; x < n - 1; x++) if (m[y][x] === m[y][x + 1] && m[y][x] === m[y + 1][x] && m[y][x] === m[y + 1][x + 1]) punkte += 3;
    // Regel 4: Anteil dunkler Module
    const dunkel = m.reduce((s, z) => s + z.filter(Boolean).length, 0);
    punkte += Math.floor(Math.abs((dunkel * 20) / (n * n) - 10)) * 10;
    return punkte;
  }
}

/** Zeilen des QR-Codes (true = dunkel). `maske` 0 bis 7 erzwingt eine Maske (für Vergleiche), sonst gewinnt die mit den wenigsten Strafpunkten. */
export function qrMatrix(text: string, maske?: number): boolean[][] {
  const bytes = Array.from(new TextEncoder().encode(text));
  let version = 0;
  for (let v = 1; v <= M_TABELLE.length; v++) {
    const kapazitaet = M_TABELLE[v - 1].bloecke.reduce((s, [n, d]) => s + n * d, 0);
    if (bytes.length <= kapazitaet - (v < 10 ? 2 : 3)) {
      version = v;
      break;
    }
  }
  if (!version) throw new Error("Der Text ist für den QR-Code zu lang.");
  const woerter = mitFehlerkorrektur(datenwoerter(bytes, version), version);
  let beste: Matrix | null = null;
  let besteStrafe = Infinity;
  for (let k = maske ?? 0; k <= (maske ?? 7); k++) {
    const m = new Matrix(version);
    m.platziere(woerter);
    m.maskieren(k);
    m.formatBits(k);
    const strafe = m.strafpunkte();
    if (strafe < besteStrafe) {
      besteStrafe = strafe;
      beste = m;
    }
  }
  return (beste as Matrix).dunkel;
}

/** SVG-Pfad aller dunklen Module (Einheiten = Module, ohne Rand) und die Kantenlänge */
export function qrPfad(text: string): { d: string; groesse: number } {
  const m = qrMatrix(text);
  let d = "";
  m.forEach((zeile, y) => {
    let x = 0;
    while (x < zeile.length) {
      if (!zeile[x]) {
        x++;
        continue;
      }
      let ende = x;
      while (ende < zeile.length && zeile[ende]) ende++;
      d += `M${x} ${y}h${ende - x}v1h-${ende - x}z`;
      x = ende;
    }
  });
  return { d, groesse: m.length };
}
